import React from 'react';
import { RouteComponentProps, Link } from '@reach/router';
import { Container } from 'bloomer/lib/layout/Container';
import { Box } from 'bloomer/lib/elements/Box';
import { Title } from 'bloomer/lib/elements/Title';
import api from '../util/api';
import Animal from '../Entities/Animal';
import Conditional from '../components/Conditional';
import LoadingSpinner from '../elements/LoadingSpinner';
import moment from 'moment';
import { Columns } from 'bloomer/lib/grid/Columns';
import { Column } from 'bloomer/lib/grid/Column';
import { Progress } from 'bloomer/lib/elements/Progress';
import { Icon } from 'bloomer/lib/elements/Icon';
import { popupErrorFromServer } from '../util/helperFunctions';

interface PropTypes extends RouteComponentProps<{}> {}

interface StateTypes {
  animals: Animal[];
  rooms: string[];
  isLoading: boolean;
}

class EvaluationsRoomList extends React.Component<PropTypes, StateTypes> {
  state: StateTypes = {
    rooms: [],
    animals: [],
    isLoading: true,
  };

  async componentDidMount() {
    try {
      const animalsProm = api.animals.get({
        active: true,
      });

      const evaluationsProm = api.evaluations.get({});

      const [animals, evaluations] = await Promise.all([animalsProm, evaluationsProm]);

      // sort alphabetically by room
      animals
        .sort((a, b) => {
          if (a.getLocationName() < b.getLocationName()) {
            return -1;
          }
          if (a.getLocationName() > b.getLocationName()) {
            return 1;
          }
          return 0;
        })
        .map(animal => animal.findAndRegisterEvaluations(evaluations));

      const rooms = [...new Set(animals.map(animal => animal.getLocationName()).map(loc => loc.trim()))];

      this.setState({
        animals,
        rooms,
        isLoading: false,
      });
    } catch (err) {
      popupErrorFromServer(err);
    }
  }

  render() {
    const { isLoading, rooms, animals } = this.state;
    const totalCompleteMorning = animals.filter(animal => animal.hasMorningEvaluation()).length;
    const totalCompleteEvening = animals.filter(animal => animal.hasEveningEvaluation()).length;

    return (
      <Container>
        <Box>
          <Title>Animal Evaluations - {moment().format('dddd MMMM DD, YYYY')}</Title>
          <Conditional check={!isLoading} alt={<LoadingSpinner />}>
            <>
              {rooms.map(room => {
                const animalsForRoom = animals.filter(animal => animal.getLocationName().trim() === room);
                const numberOfCompleteMorningEvaluations = animalsForRoom.filter(animal =>
                  animal.hasMorningEvaluation(),
                ).length;
                const numberOfCompleteEveningEvaluations = animalsForRoom.filter(animal =>
                  animal.hasEveningEvaluation(),
                ).length;
                const isComplete =
                  numberOfCompleteMorningEvaluations === animalsForRoom.length &&
                  numberOfCompleteEveningEvaluations === animalsForRoom.length;
                return (
                  <Box key={`room-${room}`}>
                    <Link to={`/evaluations/${room}`}>
                      <Columns isVCentered={true}>
                        <Column>
                          <p className="is-size-4 is-capitalized">{room}</p>
                        </Column>
                        <Column isSize="narrow">
                          <Conditional check={isComplete}>
                            <Icon isSize="large" className="fa fa-check has-text-success fa-3x" />
                          </Conditional>
                        </Column>
                        <Column isSize="1/3">
                          <p>
                            Morning Evaluations ({numberOfCompleteMorningEvaluations} / {animalsForRoom.length}){' '}
                          </p>
                          <Progress
                            isColor="warning"
                            value={numberOfCompleteMorningEvaluations}
                            max={animalsForRoom.length}
                          />
                        </Column>
                        <Column isSize="1/3">
                          <p>
                            Evening Evaluations ({numberOfCompleteEveningEvaluations} / {animalsForRoom.length})
                          </p>
                          <Progress
                            isColor="info"
                            value={numberOfCompleteEveningEvaluations}
                            max={animalsForRoom.length}
                          />
                        </Column>
                      </Columns>
                    </Link>
                  </Box>
                );
              })}
              <Box>
                <Columns>
                  <Column>
                    <p>Totals:</p>
                  </Column>
                  <Column isSize="narrow">
                    <Conditional
                      check={totalCompleteEvening === animals.length && totalCompleteMorning === animals.length}
                    >
                      <Icon isSize="large" className="fa fa-check has-text-success fa-3x" />
                    </Conditional>
                  </Column>
                  <Column isSize="1/3">
                    <p>
                      Morning Evaluations ({totalCompleteMorning} / {animals.length}){' '}
                    </p>
                    <Progress isColor="warning" value={totalCompleteMorning} max={animals.length} />
                  </Column>
                  <Column isSize="1/3">
                    <p>
                      Evening Evaluations ({totalCompleteEvening} / {animals.length})
                    </p>
                    <Progress isColor="info" value={totalCompleteEvening} max={animals.length} />
                  </Column>
                </Columns>
              </Box>
            </>
          </Conditional>
        </Box>
      </Container>
    );
  }
}

export default EvaluationsRoomList;
